<template>
  <v-container>
    <h2>{{ $t('views.merchants.mailingConfig.title') }} | {{ merchantName }}</h2>

    <v-overlay
      :value="isInitializing"
      absolute
    >
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      />
    </v-overlay>

    <v-divider class="my-3" />

    <v-row
      no-gutters
      class="mb-3"
    >
      <v-col cols="12">
        <span class="text--secondary">
          {{ $t('views.merchants.mailingConfig.lastSentAt') }}：
        </span>
        <span>{{ lastSentAtDisplay }}</span>
      </v-col>
    </v-row>

    <v-row
      align="center"
      no-gutters
      class="mb-3"
    >
      <v-col
        cols="12"
        md="8"
      >
        <span
          class="text-h3"
          style="vertical-align: middle;"
        >
          {{ $t('views.merchants.mailingConfig.enabledLabel') }}
        </span>
        <br>
        <span class="text--disabled">
          {{ $t('views.merchants.mailingConfig.enabledHint') }}
        </span>
      </v-col>
      <v-col class="text-right">
        <v-switch
          v-model="form.enabled"
          inset
          :disabled="isInitializing || isSaving"
        />
      </v-col>
    </v-row>

    <v-text-field
      v-model="form.recipient"
      outlined
      dense
      :label="$t('views.merchants.mailingConfig.recipient')"
      :rules="recipientRules"
      :disabled="isInitializing || isSaving"
      type="email"
    />

    <v-combobox
      v-model="form.ccEmails"
      outlined
      dense
      multiple
      chips
      deletable-chips
      class="cc-emails-combobox"
      :label="$t('views.merchants.mailingConfig.ccEmails')"
      :hint="$t('views.merchants.mailingConfig.ccEmailsHint')"
      persistent-hint
      :rules="ccEmailsRules"
      :disabled="isInitializing || isSaving"
      @change="normalizeCcEmails"
    />

    <v-card-actions class="d-flex justify-end mt-4 px-0">
      <v-btn
        text
        :disabled="isSaving"
        @click="onCancel"
      >
        {{ $t('views.merchants.mailingConfig.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        :loading="isSaving"
        :disabled="!isFormValid"
        @click="onSave"
      >
        {{ $t('views.merchants.mailingConfig.save') }}
      </v-btn>
    </v-card-actions>

    <v-dialog
      v-model="confirmDialog.show"
      max-width="400px"
      persistent
    >
      <v-card>
        <v-card-title>
          {{ $t('views.merchants.mailingConfig.confirmDisableTitle') }}
        </v-card-title>
        <v-card-text>
          {{ $t('views.merchants.mailingConfig.confirmDisableMessage') }}
        </v-card-text>
        <v-card-actions class="d-flex justify-end">
          <v-btn
            text
            :disabled="isSaving"
            @click="confirmDialog.show = false"
          >
            {{ $t('views.merchants.mailingConfig.cancel') }}
          </v-btn>
          <v-btn
            color="error"
            :loading="isSaving"
            @click="confirmedSave"
          >
            {{ $t('views.merchants.mailingConfig.confirmDisableYes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import axios from 'axios'
  import moment from 'moment'

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  export default {
    props: {
      merchantId: String,
      merchantName: String,
      merchant: Object,
    },
    data () {
      return {
        isInitializing: false,
        isSaving: false,
        hasExistingConfig: false,
        original: {
          sendPeriod: null,
          recipient: '',
          ccEmails: [],
          lastSentAt: null,
        },
        form: {
          enabled: true,
          recipient: '',
          ccEmails: [],
        },
        confirmDialog: { show: false },
      }
    },
    computed: {
      recipientRules () {
        return [
          v => !!v || this.$t('views.merchants.mailingConfig.recipientRequired'),
          v => EMAIL_REGEX.test(v) || this.$t('views.merchants.mailingConfig.recipientInvalid'),
        ]
      },
      ccEmailsRules () {
        return [
          v => {
            if (!Array.isArray(v) || !v.length) return true
            const invalid = v.filter(e => !EMAIL_REGEX.test(e))
            if (!invalid.length) return true
            return `${this.$t('views.merchants.mailingConfig.recipientInvalid')}: ${invalid.join(', ')}`
          },
        ]
      },
      isFormValid () {
        if (!this.form.recipient || !EMAIL_REGEX.test(this.form.recipient)) return false
        return this.form.ccEmails.every(e => EMAIL_REGEX.test(e))
      },
      nextSendPeriod () {
        return this.form.enabled ? '1w' : '0'
      },
      lastSentAtDisplay () {
        if (!this.original.lastSentAt) {
          return this.$t('views.merchants.mailingConfig.lastSentAtNone')
        }
        return moment.unix(this.original.lastSentAt).format('YYYY-MM-DD HH:mm:ss')
      },
    },
    watch: {
      merchantId: {
        immediate: true,
        handler (id) {
          if (id) this.loadConfig()
        },
      },
    },
    methods: {
      async loadConfig () {
        this.isInitializing = true
        try {
          const res = await axios.get(
            `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/merchants/${this.merchantId}/mailing-config`,
          )
          const data = res.data || {}
          this.hasExistingConfig = true
          this.original = {
            sendPeriod: data.sendPeriod || '1w',
            recipient: data.recipient || '',
            ccEmails: Array.isArray(data.ccEmails) ? [...data.ccEmails] : [],
            lastSentAt: data.lastSentAt || null,
          }
          this.form = {
            enabled: this.original.sendPeriod !== '0',
            recipient: this.original.recipient,
            ccEmails: [...this.original.ccEmails],
          }
        } catch (err) {
          const msg = err.response?.data?.message || err.message || ''
          const isNotFound = /No mailing configuration found for shop/i.test(msg)
          if (isNotFound) {
            this.hasExistingConfig = false
            this.original = { sendPeriod: '0', recipient: '', ccEmails: [], lastSentAt: null }
            this.form = {
              enabled: false,
              recipient: this.merchant?.email || '',
              ccEmails: [],
            }
          } else {
            this.$notify({
              text: `${this.$t('views.merchants.mailingConfig.loadFailure')}: ${msg}`,
              type: 'error',
            })
          }
        } finally {
          this.isInitializing = false
        }
      },
      onCancel () {
        this.$emit('close')
      },
      normalizeCcEmails (emails) {
        const cleaned = (emails || []).map(e => String(e).trim()).filter(e => e.length > 0)
        const dedup = [...new Set(cleaned)]
        if (JSON.stringify(dedup) !== JSON.stringify(emails)) {
          this.form.ccEmails = dedup
        }
      },
      onSave () {
        const wasEnabled = this.original.sendPeriod && this.original.sendPeriod !== '0'
        const willDisable = this.nextSendPeriod === '0'
        if (wasEnabled && willDisable) {
          this.confirmDialog.show = true
          return
        }
        this.persist()
      },
      confirmedSave () {
        this.confirmDialog.show = false
        this.persist()
      },
      async persist () {
        this.isSaving = true
        const recipient = (this.form.recipient || '').trim()
        const ccEmails = (this.form.ccEmails || [])
          .map(e => String(e).trim())
          .filter(e => e.length > 0)
        const body = {
          recipient,
          sendPeriod: this.nextSendPeriod,
          ccEmails,
        }
        const url = `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/merchants/${this.merchantId}/mailing-config`
        try {
          if (this.hasExistingConfig) {
            await axios.put(url, body)
          } else {
            await axios.post(url, body)
          }
          this.$notify({
            text: this.$t('views.merchants.mailingConfig.saveSuccess'),
            type: 'success',
          })
          this.original = {
            sendPeriod: body.sendPeriod,
            recipient: body.recipient,
            ccEmails: [...body.ccEmails],
            lastSentAt: this.original.lastSentAt,
          }
          this.form.recipient = body.recipient
          this.form.ccEmails = [...body.ccEmails]
          this.hasExistingConfig = true
          this.$emit('saved')
          this.$emit('close')
        } catch (err) {
          const msg = err.response?.data?.message || err.message
          this.$notify({
            text: `${this.$t('views.merchants.mailingConfig.saveFailure')}: ${msg}`,
            type: 'error',
          })
        } finally {
          this.isSaving = false
        }
      },
    },
  }
</script>

<style scoped>
/* Override global .v-select__selections uppercase rule from sass/vuetify-material/_form.sass */
.cc-emails-combobox >>> .v-select__selections {
  text-transform: none;
}
</style>
